import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// ============================================================================
// KycPage — Store KYC / business info / bank account submission
// Production-ready: client-side validation, status badges, read-only after approval.
// ============================================================================

const KOREAN_BANKS = [
  'KB국민은행', '신한은행', '우리은행', '하나은행', 'NH농협은행', 'IBK기업은행',
  'SC제일은행', '씨티은행', '카카오뱅크', '토스뱅크', '케이뱅크',
  '부산은행', '대구은행', '광주은행', '전북은행', '제주은행', '경남은행',
  '새마을금고', '신협', '우체국', '수협', '산업은행',
];

const DOC_TYPE_LABELS = {
  business_license: '사업자등록증',
  bankbook: '통장사본',
  id_card: '대표자 신분증',
};

const STATUS_META = {
  pending:   { label: '작성중',  color: 'var(--text-tertiary)', bg: '#F2F4F6' },
  submitted: { label: '검토중',  color: 'var(--warning)',       bg: 'var(--warning-light)' },
  approved:  { label: '승인완료', color: 'var(--success)',       bg: 'var(--success-light)' },
  rejected:  { label: '반려',    color: 'var(--danger)',        bg: 'var(--danger-light)' },
};

const BRN_REGEX = /^\d{10}$/;
const ACCOUNT_REGEX = /^\d{10,20}$/;

// ---------- Toast hook ----------
let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

// ---------- Field component ----------
const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
      {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
    </label>
    {children}
    {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{error}</div>}
  </div>
);

const inputStyle = (readOnly, hasError) => ({
  width: '100%',
  padding: '12px 14px',
  border: `1px solid ${hasError ? 'var(--danger)' : 'var(--divider)'}`,
  borderRadius: 12,
  fontSize: 15,
  background: readOnly ? 'var(--border)' : '#fff',
  color: 'var(--text)',
  transition: 'border-color 0.15s ease',
  outline: 'none',
});

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 14px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        color: meta.color,
        background: meta.bg,
      }}
    >
      {meta.label}
    </span>
  );
};

// ---------- Main page ----------
const KycPage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [kyc, setKyc] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [errors, setErrors] = useState({});
  const [agreed, setAgreed] = useState(false);

  // Upload modal state
  const [uploadType, setUploadType] = useState('');
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadName, setUploadName] = useState('');

  const [form, setForm] = useState({
    business_registration_number: '',
    business_name: '',
    representative_name: '',
    business_address: '',
    business_type: '',
    business_category: '',
    business_phone: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_holder: '',
    settlement_cycle: 'weekly',
  });

  const readOnly = kyc?.kyc_status === 'submitted' || kyc?.kyc_status === 'approved';

  // ---------- Load ----------
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [kycRes, docRes] = await Promise.all([
        api.get('/admin/kyc/me'),
        api.get('/admin/kyc/me/documents'),
      ]);
      const k = kycRes.data.kyc || {};
      setKyc(k);
      setForm({
        business_registration_number: k.business_registration_number || '',
        business_name: k.business_name || '',
        representative_name: k.representative_name || '',
        business_address: k.business_address || '',
        business_type: k.business_type || '',
        business_category: k.business_category || '',
        business_phone: k.business_phone || '',
        bank_name: k.bank_name || '',
        bank_account_number: k.bank_account_number || '',
        bank_account_holder: k.bank_account_holder || '',
        settlement_cycle: k.settlement_cycle || 'weekly',
      });
      setDocuments(docRes.data.documents || []);
    } catch (e) {
      console.error(e);
      addToast('KYC 정보를 불러오지 못했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---------- Validation ----------
  const validate = () => {
    const e = {};
    const brn = String(form.business_registration_number || '').replace(/\D/g, '');
    if (!brn) e.business_registration_number = '사업자등록번호를 입력해주세요';
    else if (!BRN_REGEX.test(brn)) e.business_registration_number = '사업자등록번호는 숫자 10자리여야 합니다';

    if (!form.business_name) e.business_name = '상호를 입력해주세요';
    if (!form.representative_name) e.representative_name = '대표자명을 입력해주세요';
    if (!form.business_address) e.business_address = '사업장 주소를 입력해주세요';
    if (!form.bank_name) e.bank_name = '은행을 선택해주세요';

    const acct = String(form.bank_account_number || '').replace(/\D/g, '');
    if (!acct) e.bank_account_number = '계좌번호를 입력해주세요';
    else if (!ACCOUNT_REGEX.test(acct)) e.bank_account_number = '계좌번호는 숫자 10~20자리여야 합니다';

    if (!form.bank_account_holder) e.bank_account_holder = '예금주명을 입력해주세요';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---------- Save draft ----------
  const handleSave = async () => {
    if (readOnly) return;
    if (!validate()) {
      addToast('입력값을 확인해주세요', 'error');
      return;
    }
    try {
      setSaving(true);
      await api.put('/admin/kyc/me', form);
      addToast('저장되었습니다', 'success');
      await loadData();
    } catch (e) {
      const msg = e?.response?.data?.error || '저장에 실패했습니다';
      addToast(msg, 'error');
      if (e?.response?.data?.fields) setErrors(e.response.data.fields);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Submit for review ----------
  const handleSubmit = async () => {
    if (readOnly) return;
    if (!validate()) {
      addToast('입력값을 확인해주세요', 'error');
      return;
    }
    if (!agreed) {
      addToast('약관에 동의해주세요', 'error');
      return;
    }
    const hasLicense = documents.some((d) => d.doc_type === 'business_license');
    if (!hasLicense) {
      addToast('사업자등록증을 업로드해주세요', 'error');
      return;
    }
    try {
      setSubmitting(true);
      // Save latest edits first
      await api.put('/admin/kyc/me', form);
      await api.post('/admin/kyc/me/submit');
      addToast('심사 요청이 제출되었습니다', 'success');
      await loadData();
    } catch (e) {
      const msg = e?.response?.data?.error || '제출에 실패했습니다';
      addToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Document upload ----------
  const handleUploadDoc = async () => {
    if (!uploadType || !uploadUrl) {
      addToast('문서 종류와 파일 URL을 입력해주세요', 'error');
      return;
    }
    try {
      await api.post('/admin/kyc/me/document', {
        doc_type: uploadType,
        file_url: uploadUrl,
        file_name: uploadName || uploadType,
      });
      setUploadType('');
      setUploadUrl('');
      setUploadName('');
      addToast('업로드되었습니다', 'success');
      await loadData();
    } catch (e) {
      addToast(e?.response?.data?.error || '업로드 실패', 'error');
    }
  };

  const handleDeleteDoc = async (id) => {
    if (readOnly) return;
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await api.delete(`/admin/kyc/me/document/${id}`);
      addToast('삭제되었습니다', 'success');
      await loadData();
    } catch (e) {
      addToast('삭제 실패', 'error');
    }
  };

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
        불러오는 중...
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 28px', maxWidth: 880, margin: '0 auto' }}>
      <Toast toasts={toasts} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0 }}>KYC / 사업자 정보</h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 14, marginTop: 6 }}>
            정산을 받으시려면 사업자 정보와 은행 계좌를 등록하고 심사를 받으세요.
          </p>
        </div>
        <StatusBadge status={kyc?.kyc_status || 'pending'} />
      </div>

      {/* Rejection banner */}
      {kyc?.kyc_status === 'rejected' && kyc?.kyc_rejection_reason && (
        <div style={{
          background: 'var(--danger-light)',
          border: '1px solid var(--danger)',
          borderRadius: 16,
          padding: 16,
          marginBottom: 20,
          color: 'var(--danger)',
        }}>
          <strong>반려 사유:</strong> {kyc.kyc_rejection_reason}
        </div>
      )}

      {/* Approved banner */}
      {kyc?.kyc_status === 'approved' && (
        <div style={{
          background: 'var(--success-light)',
          border: '1px solid var(--success)',
          borderRadius: 16,
          padding: 16,
          marginBottom: 20,
          color: 'var(--success)',
          fontWeight: 600,
        }}>
          KYC 심사가 완료되었습니다. 정산이 정상적으로 진행됩니다.
        </div>
      )}

      {/* Card 1: Business info */}
      <section style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-card)', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 18, color: 'var(--text)' }}>사업자 정보</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="사업자등록번호" required error={errors.business_registration_number}>
            <input
              type="text"
              placeholder="1234567890"
              value={form.business_registration_number}
              onChange={setField('business_registration_number')}
              readOnly={readOnly}
              maxLength={12}
              style={inputStyle(readOnly, errors.business_registration_number)}
            />
          </Field>
          <Field label="상호" required error={errors.business_name}>
            <input type="text" value={form.business_name} onChange={setField('business_name')} readOnly={readOnly} style={inputStyle(readOnly, errors.business_name)} />
          </Field>
          <Field label="대표자명" required error={errors.representative_name}>
            <input type="text" value={form.representative_name} onChange={setField('representative_name')} readOnly={readOnly} style={inputStyle(readOnly, errors.representative_name)} />
          </Field>
          <Field label="전화번호">
            <input type="text" placeholder="02-1234-5678" value={form.business_phone} onChange={setField('business_phone')} readOnly={readOnly} style={inputStyle(readOnly, errors.business_phone)} />
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="사업장 주소" required error={errors.business_address}>
              <input type="text" value={form.business_address} onChange={setField('business_address')} readOnly={readOnly} style={inputStyle(readOnly, errors.business_address)} />
            </Field>
          </div>
          <Field label="업태">
            <input type="text" placeholder="음식점업" value={form.business_type} onChange={setField('business_type')} readOnly={readOnly} style={inputStyle(readOnly)} />
          </Field>
          <Field label="종목">
            <input type="text" placeholder="한식" value={form.business_category} onChange={setField('business_category')} readOnly={readOnly} style={inputStyle(readOnly)} />
          </Field>
        </div>
      </section>

      {/* Card 2: Bank */}
      <section style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-card)', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 18, color: 'var(--text)' }}>은행 계좌</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="은행명" required error={errors.bank_name}>
            <select value={form.bank_name} onChange={setField('bank_name')} disabled={readOnly} style={inputStyle(readOnly, errors.bank_name)}>
              <option value="">은행을 선택하세요</option>
              {KOREAN_BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="예금주명" required error={errors.bank_account_holder}>
            <input type="text" value={form.bank_account_holder} onChange={setField('bank_account_holder')} readOnly={readOnly} style={inputStyle(readOnly, errors.bank_account_holder)} />
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="계좌번호" required error={errors.bank_account_number}>
              <input
                type="text"
                placeholder="숫자만 입력 (10~20자리)"
                value={form.bank_account_number}
                onChange={setField('bank_account_number')}
                readOnly={readOnly}
                maxLength={22}
                style={inputStyle(readOnly, errors.bank_account_number)}
              />
            </Field>
          </div>
        </div>
      </section>

      {/* Card 3: Documents */}
      <section style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-card)', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 18, color: 'var(--text)' }}>첨부 서류</h2>
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 14 }}>
          사업자등록증은 필수입니다. 통장사본, 신분증은 권장입니다.
        </p>

        {/* Upload form */}
        {!readOnly && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr 1fr auto',
            gap: 10,
            padding: 14,
            background: 'var(--bg)',
            borderRadius: 12,
            marginBottom: 14,
          }}>
            <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} style={inputStyle(false)}>
              <option value="">문서 종류</option>
              <option value="business_license">사업자등록증</option>
              <option value="bankbook">통장사본</option>
              <option value="id_card">대표자 신분증</option>
            </select>
            <input type="text" placeholder="파일 URL (https://...)" value={uploadUrl} onChange={(e) => setUploadUrl(e.target.value)} style={inputStyle(false)} />
            <input type="text" placeholder="파일명 (선택)" value={uploadName} onChange={(e) => setUploadName(e.target.value)} style={inputStyle(false)} />
            <button
              onClick={handleUploadDoc}
              style={{
                padding: '0 20px',
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              업로드
            </button>
          </div>
        )}

        {/* Docs list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 14 }}>
              업로드된 문서가 없습니다
            </div>
          )}
          {documents.map((d) => (
            <div
              key={d.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: 'var(--bg)',
                borderRadius: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                  {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
                </span>
                <a
                  href={d.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: 'var(--primary)', marginTop: 2 }}
                >
                  {d.file_name || d.file_url}
                </a>
              </div>
              {!readOnly && (
                <button
                  onClick={() => handleDeleteDoc(d.id)}
                  style={{
                    padding: '6px 12px',
                    background: 'transparent',
                    color: 'var(--danger)',
                    border: '1px solid var(--danger)',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  삭제
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Card 4: Settlement cycle */}
      <section style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-card)', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14, color: 'var(--text)' }}>정산 주기</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { v: 'daily', label: '일 정산' },
            { v: 'weekly', label: '주 정산' },
            { v: 'monthly', label: '월 정산' },
          ].map((o) => (
            <label
              key={o.v}
              style={{
                flex: '1 1 120px',
                padding: '14px 18px',
                border: `2px solid ${form.settlement_cycle === o.v ? 'var(--primary)' : 'var(--divider)'}`,
                background: form.settlement_cycle === o.v ? 'var(--primary-light)' : '#fff',
                borderRadius: 12,
                cursor: readOnly ? 'default' : 'pointer',
                textAlign: 'center',
                fontWeight: 600,
                color: form.settlement_cycle === o.v ? 'var(--primary-dark)' : 'var(--text-secondary)',
                transition: 'all 0.15s',
              }}
            >
              <input
                type="radio"
                name="settlement_cycle"
                value={o.v}
                checked={form.settlement_cycle === o.v}
                onChange={setField('settlement_cycle')}
                disabled={readOnly}
                style={{ display: 'none' }}
              />
              {o.label}
            </label>
          ))}
        </div>
      </section>

      {/* Card 5: Agreement & actions */}
      {!readOnly && (
        <section style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-card)', marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 18 }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--primary)' }}
            />
            <span style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              입력한 정보가 사실이며, 정산 수수료(PG {((kyc?.pg_fee_rate || 0.029) * 100).toFixed(1)}% + 플랫폼 {((kyc?.platform_fee_rate || 0.025) * 100).toFixed(1)}%) 약관 및 개인정보 수집·이용에 동의합니다.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={handleSave}
              disabled={saving || submitting}
              style={{
                padding: '13px 24px',
                background: '#fff',
                color: 'var(--primary)',
                border: '1.5px solid var(--primary)',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                cursor: saving || submitting ? 'not-allowed' : 'pointer',
                opacity: saving || submitting ? 0.5 : 1,
              }}
            >
              {saving ? '저장 중...' : '임시 저장'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || submitting || !agreed}
              style={{
                padding: '13px 28px',
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                cursor: saving || submitting || !agreed ? 'not-allowed' : 'pointer',
                opacity: saving || submitting || !agreed ? 0.5 : 1,
              }}
            >
              {submitting ? '제출 중...' : '심사 요청'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default KycPage;
